import React, { useEffect, useState, forwardRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  IconButton, Backdrop, Box, CircularProgress, Menu, MenuItem, ListItemIcon, Tooltip, Divider, Dialog, AppBar, Toolbar, Typography, Slide, Grid, Link, Tabs, Tab, Select, FormControl, Button, DialogContent, DialogActions, useMediaQuery
} from '@mui/material';
import {
  LogoutRounded as LogoutRoundedIcon,
  AccountCircleRounded as AccountCircleRoundedIcon,
  GroupsRounded as GroupsRoundedIcon,
  CloseRounded as CloseIcon,
  AddRounded as AddIcon,
  ScheduleRounded as ScheduleRoundedIcon,
  DashboardRounded as DashboardRoundedIcon,
  RocketLaunchRounded as RocketLaunchRoundedIcon
} from '@mui/icons-material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { socket } from './socket';
import Users from './users';
import Analytics from './analytics';
import Schedules from './schedules';
import Dashboards from './dashboards';
import Spacex from './spacex';

var api = axios.create({ withCredentials: true });

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function App() {
  const { isLoading, isAuthenticated, error, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const [loading, setLoading] = useState(0);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [current_user, setCurrentUser] = useState(null);
  const [current_dashboard, setCurrentDashboard] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openDialog, setDialogOpen] = useState(false);
  const [openSchedulesDialog, setSchedulesDialogOpen] = useState(false);
  const [openDashboardsDialog, setDashboardsDialogOpen] = useState(false);
  const [openSpacexDialog, setSpacexDialogOpen] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState('');

  const isMobile = useMediaQuery('(max-width:600px)');

  const handleDialogClickOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSchedulesDialogOpen = () => {
    setSchedulesDialogOpen(true);
  };
  const handleSchedulesDialogClose = () => {
    setSchedulesDialogOpen(false);
  };
  
  const handleDashboardsDialogOpen = () => {
    setDashboardsDialogOpen(true);
  };
  const handleDashboardsDialogClose = () => {
    setDashboardsDialogOpen(false);
  };

  const handleSpacexDialogOpen = () => {
    setSpacexDialogOpen(true);
  };
  const handleSpacexDialogClose = () => {
    setSpacexDialogOpen(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleUserMenu = (event) => {
    if (anchorEl) {
      handleClose();
    } else {
      handleClick(event);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleDashboardDialogOpen = () => {
    setSelectedDashboard('');
    setDashboardDialogOpen(true);
  };

  const handleDashboardDialogClose = () => {
    setSelectedDashboard('');
    setDashboardDialogOpen(false);
  };

  const handleDashboardSelect = (event) => {
    setSelectedDashboard(event.target.value);
    const dashboard = current_user.dashboards.find(d => d.view === event.target.value);
    const newTab = { id: uuidv4(), dashboard: dashboard };
    setTabs([...tabs, newTab]);
    setSelectedTab(newTab.id);
    handleDashboardDialogClose();
  };

  const addTab = () => {
    handleDashboardDialogOpen();
  };

  const removeTab = (tabId) => {
    if (tabs.length > 1) {
      const newTabs = tabs.filter(tab => tab.id !== tabId);
      setTabs(newTabs);
      if (selectedTab === tabId) {
        setSelectedTab(newTabs.length > 0 ? newTabs[0].id : null);
      }
    }
  };

  function getCurrentUser(controller) {
    getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0AUDIENCE,
      scope: process.env.REACT_APP_AUTH0SCOPE
    }).then((token) => {
      api.get(
        `${process.env.REACT_APP_BASEURL}/api/users/user/${user.sub}`,
        { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } }
      ).then((response) => {
        setCurrentUser(response.data);
        setCurrentDashboard(response.data.dashboards[0]);
        const initTab = { id: uuidv4(), dashboard: response.data.dashboards[0] };
        setTabs([initTab]);
        setSelectedTab(initTab.id);

        socket.emit('join', response.data._id);
        if (response.data.admin === true) {
          socket.emit('join', 'admin');
        } else {
          socket.emit('leave', 'admin');
        }
      }).catch((err) => {
        if (err.message !== 'canceled') {
          console.log(err);
        }
      });
    });
  }

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (isAuthenticated && isConnected) {
      getCurrentUser(controller);
      socket.on('user', () => { getCurrentUser(controller); });
      socket.on('dashboards', () => { getCurrentUser(controller); })
    }

    return () => {
      socket.off('user');
      socket.off('dashboards');
      controller.abort();
    };
  }, [isConnected, isAuthenticated]);

  if (isLoading) {
    return (
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }
  if (error) {
    return (
      < Box
        component="main"
        sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
      >
        {error.message}
      </Box >
    );
  }

  if (isAuthenticated) {
    if (current_user !== null && current_dashboard !== null && selectedTab !== null) {
      return (
        <>
          <ToastContainer position='bottom-right' autoClose={3000} />
          <Backdrop
            sx={{ color: '#fff', zIndex: 10000000 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {current_user.admin && (
            <Dialog
              fullScreen
              open={openDialog}
              onClose={handleDialogClose}
              TransitionComponent={Transition}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleDialogClose}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                    User Management
                  </Typography>
                </Toolbar>
              </AppBar>
              <Users isConnected={isConnected} user={current_user} />
            </Dialog>
          )}

          <Dialog
            fullScreen
            open={openSchedulesDialog}
            onClose={handleSchedulesDialogClose}
            TransitionComponent={Transition}
          >
            <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleSchedulesDialogClose}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                  Scheduled Dashboards
                </Typography>
              </Toolbar>
            </AppBar>
            <Schedules user={current_user} />
          </Dialog>

          {current_user.spacex && (
            <Dialog
              fullScreen
              open={openSpacexDialog}
              onClose={handleSpacexDialogClose}
              TransitionComponent={Transition}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={handleSpacexDialogClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                    SpaceX Intake
                  </Typography>
                </Toolbar>
              </AppBar>
              <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                <Spacex user={current_user} />
              </Box>
            </Dialog>
          )}

          {current_user.admin && (
            <Dialog
              fullScreen
              open={openDashboardsDialog}
              onClose={handleDashboardsDialogClose}
              TransitionComponent={Transition}
            >
              <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleDashboardsDialogClose}
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                    Dashboard Management
                  </Typography>
                </Toolbar>
              </AppBar>
              <Dashboards isConnected={isConnected} user={current_user} />
            </Dialog>
          )}

          <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: 'white', pt: 1, zIndex: 1 }}>
            <Grid container spacing={2} sx={{ flexGrow: 1, alignItems: 'center' }} alignContent={'center'}>
              <Grid item>
                <Link href="https://www.freightsaver.com/" target="_BLANK">
                  <Box
                    component="img"
                    sx={{
                      height: 55,
                      marginLeft: '20px' // Adjusted to accommodate the new menu icon
                    }}
                    alt="FreightSaver Logo"
                    src='/images/fs_logo.png'
                  />
                </Link>
              </Grid>
              <Grid item sx={{ flexGrow: 1, maxWidth: isMobile ? '100%' : 'calc(100% - 300px)' }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label="analytics tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ minHeight: 48 }}
                >
                  {tabs.map((tab, index) => (
                    <Tab
                      value={tab.id}
                      key={tab.id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {tab.dashboard ? tab.dashboard.title : `Tab ${index + 1}`}
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              removeTab(tab.id);
                            }}
                            disabled={tabs.length === 1}
                            sx={{ padding: 0 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      sx={{ minHeight: 48 }}
                    />
                  ))}
                  <IconButton onClick={addTab} color='primary' sx={{ padding: 0 }}>
                    <AddIcon />
                  </IconButton>
                </Tabs>
              </Grid>
              <Grid item sx={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '20px' }}>
                <Tooltip title="Account Info">
                  <IconButton onClick={toggleUserMenu} color='success' size='small'>
                    <AccountCircleRoundedIcon fontSize='large' />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  id="account-menu"
                  open={open}
                  onClose={handleClose}
                  onClick={handleClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem disabled>{`${current_user.fname} ${current_user.lname}`}</MenuItem>
                  <MenuItem disabled sx={{ fontSize: '.75rem' }}>{`${current_user.email}`}</MenuItem>
                  <Divider />
                  {
                    current_user.admin
                      ?
                      <>
                        <MenuItem onClick={handleDialogClickOpen}>
                          <ListItemIcon>
                            <GroupsRoundedIcon fontSize="small" color='primary' />
                          </ListItemIcon>
                          User Management
                        </MenuItem>
                        <MenuItem onClick={handleDashboardsDialogOpen}>
                          <ListItemIcon>
                            <DashboardRoundedIcon fontSize="small" color='primary' />
                          </ListItemIcon>
                          Dashboard Management
                        </MenuItem>
                      </>
                      :
                      <></>
                  }
                  {current_user.spacex && (
                    <MenuItem onClick={handleSpacexDialogOpen}>
                      <ListItemIcon>
                        <RocketLaunchRoundedIcon fontSize="small" color='primary' />
                      </ListItemIcon>
                      SpaceX Intake
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleSchedulesDialogOpen}>
                    <ListItemIcon>
                      <ScheduleRoundedIcon fontSize="small" color='primary' />
                    </ListItemIcon>
                    Scheduled Dashboards
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                    <ListItemIcon>
                      <LogoutRoundedIcon fontSize="small" color='error' />
                    </ListItemIcon>
                    Log Out
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Box>

          <Dialog open={dashboardDialogOpen} onClose={handleDashboardDialogClose} maxWidth="md" fullWidth>
            <DialogContent>
              <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                <Select
                  value={selectedDashboard || ''}
                  onChange={handleDashboardSelect}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxWidth: 'none',
                        width: 'auto',
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select a Dashboard
                  </MenuItem>
                  {current_user.dashboards.map((dashboard) => (
                    <MenuItem key={dashboard.view} value={dashboard.view}>
                      {dashboard.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDashboardDialogClose}>Cancel</Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ width: '100%', height: 'calc(100vh - 80px)', position: 'relative', p: 0, m: 0, mt: '80px', zIndex: 0 }}>
            {tabs.map((tab) => (
              <Box
                value={tab.id}
                key={tab.id}
                role="tabpanel"
                hidden={false} // Always render the tab panels
                id={`tabpanel-${tab.id}`}
                aria-labelledby={`tab-${tab.id}`}
                sx={{ width: '100%', height: '100%', display: selectedTab === tab.id ? 'block' : 'none' }} // Show only the selected tab
              >
                <Analytics isConnected={isConnected} dashboard={tab.dashboard ? tab.dashboard.dashboard : current_dashboard.dashboard} title={tab.dashboard ? tab.dashboard.title : current_dashboard.title} view={tab.dashboard ? tab.dashboard.view : current_dashboard.view} user={current_user} />
              </Box>
            ))}
          </Box>
        </>
      );
    }
  } else {
    loginWithRedirect();
  }
}

export default App;